import { useEffect, useRef } from 'react';

interface UseQuoteUrlSyncParams {
  loadedQuoteId: string | null;
  onLoadQuote: (priceQuoteId: string) => void;
  onUrlUpdate: (priceQuoteId: string | null) => void;
  urlQuoteId: string | undefined;
}

interface SyncSnapshot {
  loadedQuoteId: string | null;
  urlQuoteId: string | undefined;
}

/*
 * Keeps the browser URL and the quote id in memory in sync.
 *
 * Load, save, reset, or duplicate a quote and the URL updates to match. Change
 * the URL directly (a shared link, or the back button) and the screen loads
 * whatever quote it points to.
 */
export const useQuoteUrlSync = ({
  loadedQuoteId,
  onLoadQuote,
  onUrlUpdate,
  urlQuoteId,
}: UseQuoteUrlSyncParams): void => {
  const previousRef = useRef<SyncSnapshot | null>(null);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { loadedQuoteId, urlQuoteId };

    const editorChanged = previous && loadedQuoteId !== previous.loadedQuoteId;
    const urlChanged = !previous || urlQuoteId !== previous.urlQuoteId;

    if (editorChanged) {
      if ((loadedQuoteId ?? undefined) !== urlQuoteId) {
        onUrlUpdate(loadedQuoteId);
      }
      return;
    }

    if (!urlChanged) {
      return;
    }

    if (previous && urlQuoteId === undefined) {
      if (loadedQuoteId !== null) {
        onUrlUpdate(loadedQuoteId);
      }
      return;
    }

    if (urlQuoteId && urlQuoteId !== loadedQuoteId) {
      onLoadQuote(urlQuoteId);
    }
  }, [loadedQuoteId, onLoadQuote, onUrlUpdate, urlQuoteId]);
};
